<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title ref="ticketTitleRef">Ticket Manager: {{ showName }}</v-toolbar-title>
      <v-spacer />
      <span style="transform:translateY(2px)">Performance Time: {{ performanceDate }}</span>
      <v-spacer />
      <div ref="spacerRef">
      </div>
      <v-btn icon @click="exportList">
        <v-icon color="success">fal fa-file-spreadsheet</v-icon>
      </v-btn>
    </v-toolbar>
    <v-toolbar>
      <v-text-field v-model="nameFilter" label="Filter Name/Banner ID/Email" outlined dense hide-details clearable class="mr-2" style="max-width:300px" />
      <v-select v-model="statusFilter" :items="statusFilterOptions" label="Filter Status" outlined dense hide-details multiple chips small-chips deletable-chips class="mr-2" style="max-width:300px" />
      <v-select v-model="ticketTypeFilter" :items="ticketTypes" label="Filter Ticket Types" outlined dense hide-details multiple chips small-chips deletable-chips class="mr-2" style="max-width:300px" />
    </v-toolbar>
    <v-data-table :items="filteredTickets" :headers="headers">
      <template #item.tickets="{ item }">
        <v-chip v-for="{ label, tickets } of item.tickets" :key="label" label outlined class="mr-2">{{ label }} ({{ tickets }})</v-chip>
      </template>
      <template #item.status="{ item }">
        <v-menu>
          <template #activator="{ on }">
            <v-btn v-on="on">
              {{ item.status }}
              <v-icon right>fal fa-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-subheader>Change status to</v-subheader>
            <v-list-item v-for="status in statusOptions" :key="status" :disabled="status === item.status" @click="updateStatus(item, status)">
              <v-list-item-title>{{ status }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template #item.action="{ item }">
        <v-btn v-if="!['Checked-In','Cancelled'].includes(item.status)" @click="checkIn(item)">Check In</v-btn>
        <v-btn v-if="item.status !== 'Checked-In'" icon @click="editTicket(item)">
          <v-icon>fal fa-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog v-model="editDialog" width="800">
      <v-card>
        <v-card-title>Edit Reservation</v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <p>This column lists all of the default ticket types as well as any discounts in this reservation. You can add new ticket types or modify what's in the reservation by changing the numbers below.</p>
              <v-text-field v-for="{ label, count } of editCounts" :key="label" :value="count" :label="label" outlined dense type="number" step="1" @change="(v) => editUpdateCount(label, v)" />
            </v-col>
            <v-col>
              <p>Here you can move these tickets to another performance night. Just choose which night to move them to from the list, or leave it alone to leave it unchanged.</p>
              <v-select v-model="editPerformance" :items="editPerformanceOptions" label="Change Performance" outlined />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn color="success" @click="saveEdit()">Save Changes</v-btn>
          <v-spacer />
          <v-btn @click="editDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { saveAs } from 'file-saver'

export default {
  setup (props, { root }) {
    const performanceId = computed(() => root.$route.params.id)
    const showName = ref('')
    const showId = ref('')
    const performanceDate = ref('')
    const performanceExportDate = ref('')
    const nameFilter = ref('')
    const statusFilter = ref([])
    const statusFilterOptions = ref([])
    const statusOptions = ref(['Paid', 'Reserved', 'Cancelled', 'Checked-In'])
    const ticketTypeFilter = ref([])
    const ticketTypes = ref([])
    const ticketTypeOptions = ref([])
    const tickets = ref([])
    const filteredTickets = computed(() => tickets.value.filter(({ status, tickets, last, first, bannerId, email }) => {
      if (statusFilter.value && statusFilter.value.length > 0 && !statusFilter.value.includes(status)) return false
      if (ticketTypeFilter.value && ticketTypeFilter.value.length > 0 && tickets.filter(({ label }) => ticketTypeFilter.value.includes(label)).length === 0) return false
      if (nameFilter.value) {
        const regex = new RegExp(nameFilter.value, 'ig')
        if (!regex.test(`${last}, ${first} ${last} ${email} ${bannerId}`)) return false
      }
      return true
    }))
    const headers = ref([
      { text: 'Banner ID', value: 'bannerId' },
      { text: 'Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Ticket Count', value: 'total' },
      { text: 'Ticket Type(s) Chosen', value: 'tickets' },
      { text: 'Status', value: 'status' },
      { text: '', value: 'action' }
    ])
    const ticketTitleRef = ref(null)
    const spacerRef = ref(null)

    onMounted(async () => {
      const { data: showData } = await root.$feathers.service('theatre/show').find({ query: { 'performances._id': performanceId.value } })
      if (showData.length === 1) {
        showId.value = showData[0]._id
        showName.value = showData[0].name
        const rec = showData[0].performances.find(({ _id }) => _id === performanceId.value)
        if (rec) {
          const dt = new Date(rec.date)
          const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
          const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
          performanceDate.value = `${date} at ${time}`
          performanceExportDate.value = dt.toLocaleDateString('en-CA') + ' ' + time
          ticketTypeOptions.value = rec.prices.map(({ label }) => label)
        }
        showData[0].performances.forEach(({ _id, date }) => {
          if (_id === performanceId.value) return
          const dt = new Date(date)
          const dateStr = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
          const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
          editPerformanceOptions.value.push({ text: `${dateStr} at ${time}`, value: _id, date })
        })
      }
      const aggregate = [
        { $match: { 'tickets.performance': root.$route.params.id } },
        { $addFields: { tickets: { $filter: { input: '$tickets', as: 'ticket', cond: { $eq: ['$$ticket.performance', { $toObjectId: root.$route.params.id }] } } } } },
        { $addFields: {
          total: { $sum: '$tickets.tickets' },
          name: { $concat: ['$first', ' ', '$last'] },
          ticStatus: { $reduce: {
            input: '$tickets',
            initialValue: null,
            in: { $cond: {
              if: { $eq: ['$$value', null] },
              then: '$$this.status',
              else: {
                $cond: {
                  if: { $lt: ['$$this.status', '$$value'] },
                  then: '$$this.status',
                  else: '$$value'
                }
              }
            } }
          } }
        } },
        { $sort: { last: 1, first: 1 } }
      ]
      const { data } = await root.$feathers.service('theatre/ticket').find({ query: { aggregate } })
      tickets.value = data.map((row) => {
        if (row.ticStatus) row.status = row.ticStatus
        return row
      })
      for (const { status, tickets: tics } of tickets.value) {
        if (!statusFilterOptions.value.includes(status)) statusFilterOptions.value.push(status)
        for (const { tickets: t, label } of tics) {
          if (t === 0) continue
          if (!ticketTypes.value.includes(label)) {
            ticketTypes.value.push(label)
          }
        }
      }
      spacerRef.value.style.width = (ticketTitleRef.value.offsetWidth - 48) + 'px'
    })

    async function updateReservedCounts () {
      // Recalculate the number of reserved tickets
      const aggregate = [
        { $match: {
          show: showId.value
        } },
        { $unwind: '$tickets' },
        { $addFields: {
          'tickets.status': { $ifNull: ['$tickets.status', '$status'] }
        } },
        { $match: {
          'tickets.status': { $ne: 'Cancelled' }
        } },
        { $group: {
          _id: '$tickets.performance',
          reserved: { $sum: '$tickets.tickets' }
        } },
        { $sort: { _id: 1 } }
      ]
      const { data } = await root.$feathers.service('theatre/ticket').find({ query: { aggregate } })
      const patch = {}
      const arrayFilters = []
      let i = 0
      for (const { _id, reserved } of data) {
        patch[`performances.$[perf${i}].reserved`] = reserved
        arrayFilters.push({ [`perf${i}._id`]: _id })
        i++
      }
      await root.$feathers.service('theatre/show').patch(showId.value, patch, { query: { arrayFilters } })
    }

    async function updateStatus (item, status) {
      await root.$feathers.service('theatre/ticket').patch(item._id, { 'tickets.$[tic].status': status }, { query: { arrayFilters: [{ 'tic.performance': performanceId.value }] } })

      updateReservedCounts()

      const index = tickets.value.findIndex(({ _id }) => _id === item._id)
      if (index >= 0) {
        tickets.value.splice(index, 1, { ...tickets.value[index], status })
      }
    }

    async function checkIn (item) {
      updateStatus(item, 'Checked-In')
    }

    const editDialog = ref(false)
    const editId = ref('')
    const editCounts = ref([])
    const updatedCounts = ref([])
    const editPerformance = ref('same')
    const editPerformanceOptions = ref([{ text: 'Keep Same Performance', value: 'same' }])
    function editTicket (item) {
      editId.value = item._id
      editCounts.value = ticketTypeOptions.value.map((label) => {
        const rec = item.tickets.find(({ label: l }) => label === l)
        if (rec) {
          return { label, count: rec.tickets }
        } else {
          return { label, count: 0 }
        }
      })
      const discount = item.tickets.find(({ label }) => /Discount/.test(label))
      if (discount != null) {
        editCounts.value.splice(0, 0, { label: discount.label, count: discount.tickets })
      }
      editDialog.value = true
    }
    function editUpdateCount (label, count) {
      const index = updatedCounts.value.findIndex(({ label: l }) => label === l)
      if (index >= 0) {
        updatedCounts.value.splice(index, 1, { label, count })
      } else {
        updatedCounts.value.push({ label, count })
      }
    }
    async function saveEdit () {
      const rec = tickets.value.find(({ _id }) => _id === editId.value)
      const patch = {}
      const arrayFilters = []
      let i = 0
      for (const { label, count } of updatedCounts.value) {
        const ticRec = rec.tickets.find(({ label: l }) => label === l)
        if (ticRec) {
          patch[`tickets.$[tic${i}].tickets`] = count
          arrayFilters.push({ [`tic${i}.performance`]: performanceId.value, [`tic${i}.label`]: label })
          i++
        } else {
          if (!(patch.$push)) patch.$push = { tickets: [] }
          patch.$push.tickets.push({ performance: performanceId.value, date: new Date(performanceExportDate.value), label, tickets: count, price: 0, total: 0, status: rec.status })
        }
      }
      if (editPerformance.value !== 'same') {
        const perfRec = editPerformanceOptions.value.find(({ value }) => value === editPerformance.value)
        patch[`tickets.$[chg].performance`] = editPerformance.value
        patch[`tickets.$[chg].date`] = perfRec.date
        arrayFilters.push({ 'chg.performance': performanceId.value })
      }
      const updRec = await root.$feathers.service('theatre/ticket').patch(editId.value, patch, { query: { arrayFilters } })
      const index = tickets.value.findIndex(({ _id }) => _id === editId.value)
      if (index >= 0) {
        // Filter the tickets on updRec to be just the ones for this performance
        updRec.tickets = updRec.tickets.filter(({ performance }) => performance === performanceId.value)
        // Add a "total" to updRec with the total number of tickets reserved
        updRec.total = updRec.tickets.reduce((prev, { status, tickets }) => prev + (status === 'Cancelled' ? 0 : tickets), 0)
        updRec.name = updRec.first + ' ' + updRec.last
        tickets.value.splice(index, 1, updRec)
      }
      // Trigger an update to the reserved count
      updateReservedCounts()

      editDialog.value = false
    }

    async function exportList () {
      const header = [
        { header: 'Banner ID', key: 'bannerId', width: 20 },
        { header: 'Name', key: 'name', width: 30 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Ticket Count', key: 'total', width: 15 },
        { header: 'Status', key: 'status', width: 20 }
      ]
      // Go through the ticket types (limited to only those in the filter if they are filtered)
      if (ticketTypeFilter.value.length > 0) {
        ticketTypeFilter.value.forEach((text) => {
          header.push({ header: text, key: text, width: 30 })
        })
      } else {
        ticketTypes.value.forEach((text) => {
          header.push({ header: text, key: text, width: 30 })
        })
      }
      const filename = `Tickets for ${showName.value} on ${performanceExportDate.value}.xlsx`
      const rows = filteredTickets.value.map((row) => {
        const tickets = {}
        if (ticketTypeFilter.value.length > 0) {
          ticketTypeFilter.value.forEach((text) => {
            const rec = row.tickets.find(({ label }) => label === text)
            if (rec) {
              tickets[text] = rec.tickets
            }
          })
        } else {
          ticketTypes.value.forEach((text) => {
            const rec = row.tickets.find(({ label }) => label === text)
            if (rec) {
              tickets[text] = rec.tickets
            }
          })
        }
        return { ...row, ...tickets }
      })
      const data = await root.$feathers.service('export/xlsx').create({ filename, header, rows })
      saveAs(new Blob([data.buffer]), filename)
    }

    return {
      performanceId,
      showName,
      performanceDate,
      nameFilter,
      statusFilter,
      statusFilterOptions,
      statusOptions,
      ticketTypeFilter,
      ticketTypes,
      tickets,
      filteredTickets,
      headers,
      ticketTitleRef,
      spacerRef,
      updateStatus,
      checkIn,
      editDialog,
      editCounts,
      editPerformance,
      editPerformanceOptions,
      editTicket,
      editUpdateCount,
      saveEdit,
      exportList
    }
  }
}
</script>
